import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Container, Row, Column } from '@narvar/nth-grid';
import Button from '@narvar/nth-button';
import { SemibigHeader } from '@narvar/nth-header';
import { fetchAddons, toggleAddonDisplay } from '../../actions/addons';
import ReturnsModal from '../ReturnsModal';
import { MODAL_VERSION_1, MODAL_VERSION_2, MODAL_VERSION_3 } from '../../constants/pickups';

const mapStateToProps = ({
  language,
  retailerSettings: { branding: { brand_call_to_action: ctaColor } = {} } = {},
  addons: { addons, addonsDisplay },
  page: {
    pickupsModalVersion,
    initialPageLoadParams: { pickups: pickupsOverride = false } = {},
  } = {},
  returnsSettings: { returns: { disablePickups = false } = {} } = {},
}) => ({
  language,
  addons,
  addonsDisplay,
  ctaColor,
  pickupsOverride,
  disablePickups,
  pickupsModalVersion,
});

const ConfirmButton = styled(Button)`
  margin: 0 auto;
  padding: 15px;
`;

const PriceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
`;

const PriceStrikeout = styled(SemibigHeader)`
  text-decoration: line-through;
  opacity: 0.6;
`;

const Price = ({ price, discount }) => {
  if (!price) return null;
  const OriginalPrice = discount ? PriceStrikeout : SemibigHeader;
  return (
    <PriceRow>
      <OriginalPrice>{price}</OriginalPrice>
      {discount && <SemibigHeader>{discount}</SemibigHeader>}
    </PriceRow>
  );
};

const Addons = ({
  dispatch,
  language,
  addons,
  addonsDisplay,
  pickupsOverride,
  disablePickups,
  pickupsModalVersion,
}) => {
  useEffect(() => {
    window.setTimeout(() => {
      dispatch(fetchAddons());
    }, 2000);
  }, [dispatch]);

  if (
    !addons.length || // Disabled at a backend Retailer level
    (!window.pickupsEnabled && !pickupsOverride) || // Disabled at a global config level
    (disablePickups && !pickupsOverride) // Disabled at a retailer settings level
  ) {
    return null;
  }

  const [{ option: service, call_to_action_url: url }] = addons;

  const handleClick = () => {
    window.open(url, '_blank');
    dispatch(toggleAddonDisplay(service));
  };

  const isOpen = addonsDisplay[service];

  const ModalV1 = (
    <ReturnsModal
      staticBackdrop
      toggleOpen={() => dispatch(toggleAddonDisplay(service))}
      isOpen={isOpen}
      contentStyle={{ padding: '3.75rem 0' }}
    >
      <Container className="addon-modal">
        <Row>
          <Column md-columns={8} md-column-offset={2}>
            <SemibigHeader>
              {language[`returns.addons.${service}.call_to_action.subheader.${MODAL_VERSION_1}`]}
            </SemibigHeader>
            <p
              style={{ fontSize: '1.125rem', fontFamily: 'inherit', padding: '0 1rem' }}
              dangerouslySetInnerHTML={{
                __html:
                  language[`returns.addons.${service}.call_to_action.body.${MODAL_VERSION_1}`],
              }}
            />
            <Price
              price={language[`returns.addons.${service}.price`]}
              discount={language[`returns.addons.${service}.discount`]}
            />
            <Column md-columns={8} md-column-offset={2} style={{ marginTop: '1rem' }}>
              <ConfirmButton
                big
                onClick={handleClick}
                className="narvar-btn-primary addon-confirm-btn"
                s={{ fontSize: 'inherit', fontFamily: 'inherit' }}
              >
                {language[`returns.addons.${service}.call_to_action.confirm.${MODAL_VERSION_1}`]}
              </ConfirmButton>
            </Column>
          </Column>
        </Row>
      </Container>
    </ReturnsModal>
  );

  const ModalV2 = (
    <ReturnsModal
      staticBackdrop
      toggleOpen={() => dispatch(toggleAddonDisplay(service))}
      isOpen={isOpen}
      contentStyle={{ padding: '3.75rem 0' }}
    >
      <Container className="addon-modal">
        <Row>
          <Column md-columns={8} md-column-offset={2}>
            <SemibigHeader>
              {language[`returns.addons.${service}.call_to_action.subheader.${MODAL_VERSION_2}`]}
            </SemibigHeader>
            <p
              style={{ fontSize: '1.125rem', fontFamily: 'inherit', padding: '0 1rem' }}
              dangerouslySetInnerHTML={{
                __html:
                  language[`returns.addons.${service}.call_to_action.body.${MODAL_VERSION_2}`],
              }}
            />
            <Price
              price={language[`returns.addons.${service}.price`]}
              discount={language[`returns.addons.${service}.discount`]}
            />
            <Column md-columns={8} md-column-offset={2} style={{ marginTop: '1rem' }}>
              <ConfirmButton
                big
                onClick={handleClick}
                className="narvar-btn-primary addon-confirm-btn"
                s={{ fontSize: 'inherit', fontFamily: 'inherit' }}
              >
                {language[`returns.addons.${service}.call_to_action.confirm.${MODAL_VERSION_2}`]}
              </ConfirmButton>
            </Column>
          </Column>
        </Row>
      </Container>
    </ReturnsModal>
  );

  const ModalV3 = (
    <ReturnsModal
      staticBackdrop
      toggleOpen={() => dispatch(toggleAddonDisplay(service))}
      isOpen={isOpen}
      contentStyle={{ padding: '3.75rem 0' }}
    >
      <Container className="addon-modal">
        <Row>
          <Column md-columns={8} md-column-offset={2}>
            <SemibigHeader>
              {language[`returns.addons.${service}.call_to_action.subheader.${MODAL_VERSION_3}`]}
            </SemibigHeader>
            <p
              style={{ fontSize: '1.125rem', fontFamily: 'inherit', padding: '0 1rem' }}
              dangerouslySetInnerHTML={{
                __html:
                  language[`returns.addons.${service}.call_to_action.body.${MODAL_VERSION_3}`],
              }}
            />
            <Price
              price={language[`returns.addons.${service}.price`]}
              discount={language[`returns.addons.${service}.discount`]}
            />
            <Column md-columns={8} md-column-offset={2} style={{ marginTop: '1rem' }}>
              <ConfirmButton
                big
                onClick={handleClick}
                className="narvar-btn-primary addon-confirm-btn"
                s={{ fontSize: 'inherit', fontFamily: 'inherit' }}
              >
                {language[`returns.addons.${service}.call_to_action.confirm.${MODAL_VERSION_3}`]}
              </ConfirmButton>
            </Column>
          </Column>
        </Row>
      </Container>
    </ReturnsModal>
  );

  switch (pickupsModalVersion) {
    case MODAL_VERSION_1:
      return ModalV1;
    case MODAL_VERSION_2:
      return ModalV2;
    case MODAL_VERSION_3:
      return ModalV3;
    default:
      return ModalV3;
  }
};

Addons.defaultProps = {
  ctaColor: '#000000',
  pickupsOverride: false,
  disablePickups: false,
  pickupsModalVersion: MODAL_VERSION_3,
};

Addons.propTypes = {
  dispatch: PropTypes.func.isRequired,
  language: PropTypes.object.isRequired,
  addons: PropTypes.array.isRequired,
  addonsDisplay: PropTypes.object.isRequired,
  ctaColor: PropTypes.string,
  pickupsOverride: PropTypes.bool,
  disablePickups: PropTypes.bool,
  pickupsModalVersion: PropTypes.string,
};

Price.defaultProps = {
  price: null,
  discount: null,
};

Price.propTypes = {
  price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  discount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default connect(mapStateToProps)(Addons);
